import { 
  Store, Calendar, Users, CreditCard, TrendingUp, Package, Brain, Zap, 
  Settings, Globe, Smartphone, Activity, Shield, BarChart3, Database, 
  CheckCircle, Lock, Clock, Star, Building2
} from 'lucide-react';
import { ProductConfigs } from './types';

export const businessManagementBeautyConfigs: ProductConfigs = {
  'vagaro-cloud-platform': {
    keyBenefits: [
      {
        icon: Store,
        title: 'Cloud-Based Beauty Business Management',
        description: 'Comprehensive cloud platform for beauty salons and spas with complete business management capabilities.',
        metrics: '95% operational efficiency improvement',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Calendar,
        title: 'Advanced Appointment Scheduling',
        description: 'Intelligent scheduling system with automated booking, staff optimization, and customer preference management.',
        metrics: '90% reduction in scheduling conflicts',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Customer Relationship Excellence',
        description: 'Comprehensive CRM with customer profiles, service history, preferences, and automated communication.',
        metrics: '85% customer retention increase',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: CreditCard,
        title: 'Integrated Payment Processing',
        description: 'Seamless payment solutions with multiple payment methods, recurring billing, and financial reporting.',
        metrics: '99.9% payment success rate',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Business Analytics Dashboard',
        description: 'Advanced analytics tracking revenue, performance metrics, customer satisfaction, and growth opportunities.',
        metrics: 'Real-time insights from 100+ KPIs',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Package,
        title: 'Inventory Management System',
        description: 'Smart inventory tracking with automated reordering, cost management, and product performance analytics.',
        metrics: '75% inventory optimization',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Business Intelligence',
        description: 'Machine learning algorithms providing predictive analytics and business optimization recommendations.',
        metrics: '40% improvement in business decisions',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Marketing Automation Engine',
        description: 'Intelligent marketing campaigns with customer segmentation, automated follow-ups, and retention strategies.',
        metrics: '300% increase in marketing ROI',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Workflow Builder',
        description: 'Flexible workflow automation enabling businesses to create custom processes and operational procedures.',
        metrics: 'Unlimited custom workflows',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Management',
        description: 'Centralized management for multiple beauty business locations with unified reporting and operations.',
        metrics: 'Manage unlimited locations',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Business Platform',
        description: 'Complete mobile solution for staff and customers with booking, payments, and business management.',
        metrics: '4.8/5 mobile satisfaction rating',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Activity,
        title: 'Performance Optimization Suite',
        description: 'Advanced performance tracking for staff, services, and business health with optimization recommendations.',
        metrics: 'Track 80+ performance indicators',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'meevo2-salon-platform': {
    keyBenefits: [
      {
        icon: Building2,
        title: 'Next-Generation Salon Management',
        description: 'Advanced salon management platform with comprehensive business operations, staff coordination, and customer experience.',
        metrics: '92% salon efficiency improvement',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Calendar,
        title: 'Intelligent Scheduling System',
        description: 'Smart appointment scheduling with AI optimization, staff availability, and customer preference learning.',
        metrics: '88% scheduling optimization',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Advanced Client Management',
        description: 'Sophisticated client database with detailed profiles, service history, and personalized care protocols.',
        metrics: '90% client satisfaction increase',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: TrendingUp,
        title: 'Revenue Optimization Tools',
        description: 'Advanced revenue management with pricing strategies, upselling tools, and profit maximization analytics.',
        metrics: '70% revenue increase average',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: Star,
        title: 'Service Excellence Platform',
        description: 'Quality management system ensuring consistent service delivery and customer satisfaction optimization.',
        metrics: '4.9/5 service quality rating',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Package,
        title: 'Comprehensive Inventory Control',
        description: 'Advanced inventory management with automated purchasing, cost tracking, and product performance analytics.',
        metrics: '80% inventory efficiency gain',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Salon Intelligence',
        description: 'Machine learning algorithms optimizing salon operations, staff scheduling, and customer experience.',
        metrics: 'Predictive accuracy of 91%',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Automated Workflow Engine',
        description: 'Intelligent automation of salon workflows, notifications, and operational processes.',
        metrics: '250% workflow efficiency',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Salon Configuration',
        description: 'Flexible salon setup with custom services, pricing, staff roles, and operational procedures.',
        metrics: 'Unlimited salon customisation',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Multi-Salon Chain Management',
        description: 'Centralized management for salon chains with unified operations, reporting, and brand consistency.',
        metrics: 'Manage multiple salon locations',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Salon Operations',
        description: 'Complete mobile platform for staff and clients with booking, payments, and salon management.',
        metrics: '4.8/5 mobile platform rating',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Activity,
        title: 'Performance Analytics Suite',
        description: 'Comprehensive performance tracking for staff productivity, service quality, and business metrics.',
        metrics: 'Track 90+ performance metrics',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'glossgenius-studio-platform': {
    keyBenefits: [
      {
        icon: Star,
        title: 'Beauty Professional Suite',
        description: 'Complete platform designed for beauty professionals with booking, payments, client management, and business tools.',
        metrics: '94% professional satisfaction',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Calendar,
        title: 'Professional Booking System',
        description: 'Advanced booking platform with client self-scheduling, automated reminders, and availability optimization.',
        metrics: '87% booking efficiency improvement',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: CreditCard,
        title: 'Integrated Payment Solutions',
        description: 'Seamless payment processing with multiple payment methods, tips, packages, and subscription management.',
        metrics: '99.8% payment success rate',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Users,
        title: 'Client Relationship Management',
        description: 'Comprehensive client profiles with service history, preferences, notes, and communication tracking.',
        metrics: '88% client retention improvement',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: TrendingUp,
        title: 'Business Growth Analytics',
        description: 'Advanced analytics tracking revenue, client growth, service performance, and business optimization opportunities.',
        metrics: 'Real-time business insights',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile-First Experience',
        description: 'Optimized mobile platform for professionals and clients with complete business management on-the-go.',
        metrics: '4.9/5 mobile experience rating',
        color: 'from-teal-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Business Optimization',
        description: 'Machine learning algorithms providing intelligent scheduling, pricing recommendations, and client insights.',
        metrics: '45% business optimization improvement',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Marketing Automation Tools',
        description: 'Intelligent marketing campaigns with client segmentation, automated promotions, and retention strategies.',
        metrics: '280% marketing effectiveness increase',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Service Configuration',
        description: 'Flexible service setup with custom pricing, duration, add-ons, and professional service packages.',
        metrics: 'Unlimited service customisation',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Support',
        description: 'Support for beauty professionals operating across multiple locations with unified management.',
        metrics: 'Seamless multi-location operations',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Package,
        title: 'Inventory & Product Management',
        description: 'Smart inventory tracking with product sales, cost management, and retail integration.',
        metrics: '70% inventory optimization',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Activity,
        title: 'Professional Performance Analytics',
        description: 'Detailed performance tracking with productivity metrics, client satisfaction, and growth insights.',
        metrics: 'Comprehensive performance insights',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  }
};